import type {
    CommentCollaborator,
    CommentFile,
    Entity,
} from '@modules/build-hub-comment/types';

/** Placeholder shape: [type:id] — e.g. [user:4231], [file:817263] */
const PLACEHOLDER_RE = /\[(?<type>[a-z]+):(?<id>[\w\-.:]+)\]/g;

/** Robust label escaping for regex */
function escapeRegex(s: string) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Encode: replace each "@Label" (for known entities) with "[type:id]".
 * - Only replaces when the text literally matches "@{label}" (case-sensitive).
 * - Safe even if the same label appears multiple times.
 */
export function encodeMessage(message: string, entities: Entity[]): string {
    let out = message;

    // Sort by label length desc to avoid partial replacements (e.g. Bob vs Bobby)
    const byLen = [...entities].sort((a, b) => b.label.length - a.label.length);

    for (const ent of byLen) {
        const needle = new RegExp(`@${escapeRegex(ent.label)}`, 'g');
        const placeholder = `[${ent.type}:${ent.id}]`;
        out = out.replace(needle, placeholder);
    }
    return out;
}

/**
 * Decode: replace each "[type:id]" with "@Label" using supplied entities.
 * - Unknown placeholders are left as-is (future-proof).
 */
export function decodeMessage(message: string, entities: Entity[]): string {
    if (!entities.length) return message;

    const map = new Map<string, string>();
    for (const e of entities) map.set(`${e.type}:${e.id}`, e.label);

    return message.replace(PLACEHOLDER_RE, (match, ...args) => {
        const groups = args.at(-1) as
            | { type?: string; id?: string }
            | undefined;
        const key =
            groups?.type && groups?.id ? `${groups.type}:${groups.id}` : '';
        const label = key ? map.get(key) : undefined;
        return label ? `@${label}` : match;
    });
}

export function toEntities(
    mentions: CommentCollaborator[],
    files: CommentFile[],
    owner?: { id: number | string; name: string }
): Entity[] {
    const o: Entity[] = owner
        ? [{ type: 'owner', id: String(owner.id), label: owner.name }]
        : [];

    const m: Entity[] = mentions.map((m) => {
        const name = (m.name ?? '').trim();
        const email = (m.email ?? '').trim();
        const label = name || email || `User ${m.id}`;
        return {
            type: 'user' as const,
            id: String(m.id),
            label,
        };
    });

    const f: Entity[] = files.map((f) => ({
        type: 'file' as const,
        id: String(f.id),
        label: f.title || `File ${f.id}`,
    }));

    return [...o, ...m, ...f];
}
